import './CheckoutEvent.css'
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Loginpopup from "../Navbar/Loginmodal";
import iziToast from 'izitoast';
import { FaLocationDot, FaArrowLeft } from "react-icons/fa6";
import { SlCalender } from "react-icons/sl";
import 'izitoast/dist/css/iziToast.min.css';

export default function CheckoutEvent() {
    const navigate = useNavigate();

    // State management
    const [checkoutData, setCheckoutData] = useState(null);
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [loading, setLoading] = useState(false);
    const [useCarrotBalance, setUseCarrotBalance] = useState(true);

    // Constants
    const freePlatformFee = 50.00;
    const gstPercentage = 18;
    const carrotDiscount = 50.00;

    // User data
    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("userData"));
        } catch {
            return null;
        }
    }, []);

    // Utility function to format Indian currency
    const formatIndianCurrency = useCallback((amount) => {
        if (amount === 0) return "₹0.00";

        const [whole, decimal = "00"] = parseFloat(amount)
            .toFixed(2) // Ensures two decimal digits
            .split(".");

        const lastThree = whole.substring(whole.length - 3);
        const otherNumbers = whole.substring(0, whole.length - 3);

        const formattedWhole = otherNumbers
            ? otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree
            : lastThree;

        return `₹${formattedWhole}.${decimal}`;
    }, []);


    // Calculate totals
    const calculations = useMemo(() => {
        if (!checkoutData) return { finalAmount: 0, totalQty: 0 };

        const ticketTotal = checkoutData.selectedTickets.reduce((total, ticket) => {
            return total + (parseFloat(ticket.price) || 0) * ticket.quantity;
        }, 0);

        const totalQty = checkoutData.selectedTickets.reduce((sum, ticket) => sum + ticket.quantity, 0);

        // For free tickets, fixed pricing: 50 rs + 18% GST = 59 rs
        if (checkoutData.isFreeTiket) {
            const gstAmount = (freePlatformFee * gstPercentage) / 100;
            const finalAmount = freePlatformFee + gstAmount;

            return {
                ticketTotal: 0,
                totalQty,
                platformFee: freePlatformFee,
                gstAmount,
                carrotDiscount: 0,
                finalAmount,
                isFreeTiket: true
            };
        }

        // For paid tickets
        const carrotDiscountAmount = useCarrotBalance ? carrotDiscount : 0;
        const finalAmount = Math.max(0, ticketTotal + 5.9 - carrotDiscountAmount);

        return {
            ticketTotal,
            totalQty,
            platformFee: 5.9,
            gstAmount: 0,
            carrotDiscount: carrotDiscountAmount,
            finalAmount,
            isFreeTiket: false
        };
    }, [checkoutData, useCarrotBalance]);

    // Script loader
    const loadScripts = useCallback((src) => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = src;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    }, []);

    // Create Razorpay order
    const createRazorpayOrder = useCallback(async (amount) => {
        try {
            const formdata = new FormData();
            formdata.append("user_id", userdata?.id || "");
            formdata.append("amount", amount);

            const response = await axios.post(
                "https://partywitty.com/master/APIs/ClubEvent/createOrder",
                formdata
            );

            return response.data.order_id;
        } catch (error) {
            console.error("Error creating Razorpay order:", error);
            throw new Error("Failed to create payment order");
        }
    }, [userdata]);

    // Verify Razorpay payment
    const verifyRazorpayPayment = useCallback(async (razorpayResponse) => {
        try {
            const formdata = new FormData();
            formdata.append("response", JSON.stringify(razorpayResponse));

            const response = await axios.post(
                "https://partywitty.com/master/APIs/ClubEvent/verifyRzpPayment",
                formdata
            );

            const verificationResponse = response.data;

            if (verificationResponse.status) {
                iziToast.success({
                    title: 'Success',
                    message: verificationResponse.msg,
                    timeout: 5000,
                    position: 'topCenter',
                });
            } else {
                iziToast.error({
                    title: 'Error',
                    message: verificationResponse.msg,
                    timeout: 5000,
                    position: 'topCenter',
                });
            }
        } catch (error) {
            console.error("Error verifying payment:", error);
            iziToast.error({
                title: 'Error',
                message: 'An error occurred while verifying the payment.',
                timeout: 5000,
                position: 'topCenter',
            });
        }
    }, []);

    // Handle Razorpay payment
    const handleRazorpayScreen = useCallback(async (orderId, amount) => {
        try {
            const res = await loadScripts("https://checkout.razorpay.com/v1/checkout.js");
            if (!res) {
                throw new Error("Failed to load Razorpay script");
            }

            const options = {
                key: 'rzp_live_eF5Szxh3Z6WnaS',
                amount: amount * 100,
                currency: "INR",
                name: "PartyWitty",
                description: "Event Ticket Booking",
                order_id: orderId,
                handler: async function (response) {
                    await verifyRazorpayPayment(response);
                    window.location.href = "/event/thank-you";
                },
                prefill: {
                    name: userdata?.name || "Guest",
                    email: userdata?.email || "guest@partywitty.com",
                },
                theme: {
                    color: "#f4c438",
                },
                modal: {
                    ondismiss: async function () {
                        await verifyRazorpayPayment({ status: "failed" });
                        iziToast.error({
                            title: 'Payment Cancelled',
                            message: 'Payment was cancelled or failed',
                            timeout: 3000,
                            position: 'topCenter',
                        });
                    }
                }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (error) {
            console.error("Error opening Razorpay:", error);
            iziToast.error({
                title: 'Payment Error',
                message: 'Error processing payment. Please try again.',
                timeout: 3000,
                position: 'topCenter',
            });
        }
    }, [loadScripts, verifyRazorpayPayment, userdata]);

    // Handle form submission
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (!userdata) {
            setShowLoginPopup(true);
            return;
        }

        if (!checkoutData) {
            iziToast.error({
                title: 'Error',
                message: 'No ticket data found',
                timeout: 3000,
                position: 'topCenter',
            });
            return;
        }

        setLoading(true);

        try {
            const qty = checkoutData.selectedTickets.map(ticket => ticket.quantity).join(',');
            const ticket_id = checkoutData.selectedTickets.map(ticket => ticket.id).join(',');

            const formdata = new FormData();
            formdata.append("user_id", userdata.id);
            formdata.append("qty", qty);
            formdata.append("ticket_id", ticket_id);
            formdata.append("platform", "web");
            formdata.append("use_carrot_balance", useCarrotBalance ? "1" : "0");

            const response = await axios.post(
                "https://partywitty.com/master/APIs/ClubEvent/placeOrder",
                formdata
            );

            const data = response.data;

            if (data.status) {
                if (data.data.rzp_order_id === "") {
                    // Free booking
                    window.location.href = "/event/thank-you";
                } else {
                    // Paid booking
                    await handleRazorpayScreen(data.data.rzp_order_id, data.data.net_payable_amount);
                }
            } else {
                throw new Error(data.msg || "Failed to book tickets");
            }
        } catch (error) {
            console.error("Error placing order:", error);
            iziToast.error({
                title: 'Booking Error',
                message: error.message || 'Failed to book tickets. Please try again.',
                timeout: 5000,
                position: 'topCenter',
            });
        } finally {
            setLoading(false);
        }
    }, [userdata, checkoutData, useCarrotBalance, handleRazorpayScreen]);

    // Handle checkbox change
    const handleCheckboxChange = useCallback((e) => {
        setUseCarrotBalance(e.target.checked);
    }, []);

    // Go back to ticket selection
    const handleGoBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    // Load checkout data from localStorage
    useEffect(() => {
        const savedCheckoutData = localStorage.getItem('checkoutData');
        if (savedCheckoutData) {
            try {
                setCheckoutData(JSON.parse(savedCheckoutData));
            } catch (error) {
                console.error("Error parsing checkout data:", error);
                navigate('/');
            }
        } else {
            navigate('/');
        }
    }, [navigate]);

    if (!checkoutData) {
        return (
            <div className="checkout-page">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    return (
        <>
            <div className="event-checkout-page">
                <div className="event-checkout-container">
                    <div className="event-checkout-wrapper">
                        <div className="event-checkout-header">
                            <button onClick={handleGoBack} className="event-back-btn">
                                <FaArrowLeft />
                            </button>
                            <h2>Order Summay</h2>
                        </div>

                        <div className="event-checkout-content">
                            {/* Event Information */}
                            <div className="event-checkout-event-info">
                                <h4>
                                    {checkoutData.eventDetails.name}
                                    {checkoutData.eventDetails.categoryName && (
                                        <span> ({checkoutData.eventDetails.categoryName})</span>
                                    )}
                                </h4>
                                <p>{checkoutData.eventDetails.clubName}</p>

                                <div className='event-checkout-event-details'>
                                    <p className="event-checkout-event-address">
                                        <FaLocationDot />
                                        {checkoutData.eventDetails?.clubArea} {","} {checkoutData.eventDetails?.clubCity}
                                    </p>
                                    <p>
                                        <SlCalender />
                                        {checkoutData.eventDetails?.event_date} | {checkoutData.eventDetails?.event_time}
                                    </p>
                                </div>
                            </div>

                            {/* Selected Tickets */}
                            <div className="event-checkout-tickets-section">
                                <h3>Selected Tickets</h3>
                                <div className="event-selected-tickets-list">
                                    {checkoutData.selectedTickets.map((ticket) => (
                                        <div key={ticket.id} className="event-selected-ticket-item">
                                            <div className="event-ticket-details">
                                                <h5>{ticket.name}</h5>
                                                <p className="event-ticket-description" dangerouslySetInnerHTML={{ __html: ticket.des }} />
                                                <div className="event-ticket-quantity">
                                                    Quantity: {ticket.quantity}
                                                </div>
                                            </div>
                                            <div className="event-ticket-price">
                                                {parseFloat(ticket.price) === 0
                                                    ? ""
                                                    : `${formatIndianCurrency(ticket.price)} × ${ticket.quantity}`
                                                }
                                                {parseFloat(ticket.price) > 0 && (
                                                    <div className="event-ticket-subtotal">
                                                        = {formatIndianCurrency(parseFloat(ticket.price) * ticket.quantity)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Carrot Balance Checkbox - Only show for paid tickets */}
                            {!calculations.isFreeTiket && calculations.totalQty > 0 && (
                                <div className="event-checkout-carrot-balance">
                                    <label className="event-carrot-checkbox-container">
                                        <input
                                            type="checkbox"
                                            checked={useCarrotBalance}
                                            onChange={handleCheckboxChange}
                                            className="event-carrot-checkbox"
                                        />
                                        <span className="event-checkmark"></span>
                                        Use Carrot Balance (Save {formatIndianCurrency(carrotDiscount)})
                                    </label>
                                </div>
                            )}

                            {/* Price Breakdown */}
                            <div className="event-checkout-price-breakdown">
                                <h3>Price Summary</h3>

                                <div className="event-price-details">
                                    {calculations.isFreeTiket ? (
                                        <>
                                            {/* <div className="event-price-item">
                                                <span>Ticket(s):</span>
                                                <span></span>
                                            </div> */}
                                            <div className="event-price-item">
                                                <span>Platform Fee:</span>
                                                <span>{formatIndianCurrency(calculations.platformFee)}</span>
                                            </div>
                                            <div className="event-price-item">
                                                <span>GST ({gstPercentage}%):</span>
                                                <span>{formatIndianCurrency(calculations.gstAmount)}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="event-price-item">
                                                <span>Ticket Total:</span>
                                                <span>{formatIndianCurrency(calculations.ticketTotal)}</span>
                                            </div>
                                            <div className="event-price-item">
                                                <span>Platform Fee:</span>
                                                <span>{formatIndianCurrency(calculations.platformFee)}</span>
                                            </div>
                                            {useCarrotBalance && calculations.carrotDiscount > 0 && (
                                                <div className="event-price-item discount">
                                                    <span>Carrot Discount:</span>
                                                    <span>-{formatIndianCurrency(calculations.carrotDiscount)}</span>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    <div className="event-price-item total">
                                        <span>Total Amount:</span>
                                        <span>{formatIndianCurrency(calculations.finalAmount)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Button */}
                            <div className="event-checkout-footer">
                                <div className='event-checkout-footer-wrapper'>

                                    <div className="event-checkout-total-display">
                                        <div className="event-total-amount">
                                            {formatIndianCurrency(calculations.finalAmount)}
                                        </div>
                                        <div className="event-total-tickets">
                                            {calculations.totalQty} {calculations.totalQty === 1 ? 'Ticket' : 'Tickets'}
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        className="event-pay-now-btn"
                                    >
                                        {loading ? 'Processing...' : calculations.finalAmount === 0 ? 'Book Now' : 'Pay Now'}
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showLoginPopup && (
                <Loginpopup onClose={() => setShowLoginPopup(false)} />
            )}
        </>
    );
}